/**
 * Enhanced error handling service for better user experience
 * Provides error categorization, user-friendly messages, and retry logic
 */

/**
 * Error types for categorization
 */
export const ERROR_TYPES = {
  NETWORK: 'network',
  NOT_FOUND: 'not_found',
  PERMISSION: 'permission',
  VALIDATION: 'validation',
  SERVER: 'server',
  CLIENT: 'client',
  UNKNOWN: 'unknown'
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Categorize error based on error object or message
 */
export const categorizeError = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;

  const message = error.message || error.toString() || '';
  const code = error.code || '';

  // Network errors
  if (
    message.includes('Network') ||
    message.includes('fetch') ||
    message.includes('connection') ||
    code === 'network-request-failed'
  ) {
    return ERROR_TYPES.NETWORK;
  }

  // Not found errors
  if (
    message.includes('not found') ||
    message.includes('404') ||
    code === 'not-found' ||
    error.status === 404
  ) {
    return ERROR_TYPES.NOT_FOUND;
  }

  // Permission errors
  if (
    message.includes('permission') ||
    message.includes('unauthorized') ||
    message.includes('forbidden') ||
    code === 'permission-denied' ||
    error.status === 403 ||
    error.status === 401
  ) {
    return ERROR_TYPES.PERMISSION;
  }

  // Validation errors
  if (
    message.includes('validation') ||
    message.includes('invalid') ||
    code === 'invalid-argument' ||
    error.status === 400
  ) {
    return ERROR_TYPES.VALIDATION;
  }

  // Server errors
  if (
    message.includes('server') ||
    message.includes('internal') ||
    code === 'internal' ||
    (error.status >= 500 && error.status < 600)
  ) {
    return ERROR_TYPES.SERVER;
  }

  // Client errors
  if (error.status >= 400 && error.status < 500) {
    return ERROR_TYPES.CLIENT;
  }

  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get user-friendly error messages based on error type and context
 */
export const getErrorMessage = (error, context = 'general') => {
  const errorType = categorizeError(error);
  
  const messages = {
    [ERROR_TYPES.NETWORK]: {
      general: {
        title: 'Connection Problem',
        message: 'Unable to connect to our servers. Please check your internet connection and try again.',
        suggestion: 'Make sure you have a stable internet connection.',
        canRetry: true,
        severity: ERROR_SEVERITY.MEDIUM
      },
      profile: {
        title: 'Unable to Load Profile',
        message: 'We couldn\'t load this user\'s profile due to a connection issue.',
        suggestion: 'Please check your internet connection and try again.',
        canRetry: true,
        severity: ERROR_SEVERITY.MEDIUM
      }
    },
    [ERROR_TYPES.NOT_FOUND]: {
      general: {
        title: 'Not Found',
        message: 'The requested information could not be found.',
        suggestion: 'Please check the link and try again.',
        canRetry: false,
        severity: ERROR_SEVERITY.LOW
      },
      profile: {
        title: 'Profile Not Found',
        message: 'This user\'s profile could not be found. They may have removed their profile or the link may be incorrect.',
        suggestion: 'Please check the link or try browsing other profiles.',
        canRetry: false,
        severity: ERROR_SEVERITY.LOW
      }
    },
    [ERROR_TYPES.PERMISSION]: {
      general: {
        title: 'Access Denied',
        message: 'You don\'t have permission to access this information.',
        suggestion: 'Please make sure you\'re logged in with the correct account.',
        canRetry: false,
        severity: ERROR_SEVERITY.MEDIUM
      },
      profile: {
        title: 'Profile Not Available',
        message: 'This user\'s profile is not available for viewing.',
        suggestion: 'The user may have restricted access to their profile.',
        canRetry: false,
        severity: ERROR_SEVERITY.LOW
      }
    },
    [ERROR_TYPES.VALIDATION]: {
      general: {
        title: 'Invalid Request',
        message: 'The request contains invalid information.',
        suggestion: 'Please check your input and try again.',
        canRetry: false,
        severity: ERROR_SEVERITY.LOW
      },
      profile: {
        title: 'Invalid Profile Link',
        message: 'The profile link appears to be invalid or corrupted.',
        suggestion: 'Please check the link and try again.',
        canRetry: false,
        severity: ERROR_SEVERITY.LOW
      }
    },
    [ERROR_TYPES.SERVER]: {
      general: {
        title: 'Server Error',
        message: 'Our servers are experiencing issues. Please try again in a few moments.',
        suggestion: 'If the problem persists, please contact support.',
        canRetry: true,
        severity: ERROR_SEVERITY.HIGH
      },
      profile: {
        title: 'Unable to Load Profile',
        message: 'We\'re experiencing technical difficulties loading this profile.',
        suggestion: 'Please try again in a few moments.',
        canRetry: true,
        severity: ERROR_SEVERITY.HIGH
      }
    },
    [ERROR_TYPES.CLIENT]: {
      general: {
        title: 'Request Error',
        message: 'There was a problem with your request.',
        suggestion: 'Please try again or refresh the page.',
        canRetry: true,
        severity: ERROR_SEVERITY.MEDIUM
      },
      profile: {
        title: 'Profile Load Error',
        message: 'There was a problem loading this profile.',
        suggestion: 'Please try again or go back to the directory.',
        canRetry: true,
        severity: ERROR_SEVERITY.MEDIUM
      }
    },
    [ERROR_TYPES.UNKNOWN]: {
      general: {
        title: 'Something went wrong',
        message: 'An unexpected error occurred.',
        suggestion: 'Please try again or refresh the page.',
        canRetry: true,
        severity: ERROR_SEVERITY.MEDIUM
      },
      profile: {
        title: 'Unable to Load Profile',
        message: 'Something went wrong while loading this profile.',
        suggestion: 'Please try again or go back to the directory.',
        canRetry: true,
        severity: ERROR_SEVERITY.MEDIUM
      }
    }
  };

  return messages[errorType]?.[context] || messages[errorType]?.general || messages[ERROR_TYPES.UNKNOWN].general;
};

/**
 * Enhanced retry logic with multiple strategies and circuit breaker pattern
 */
export class RetryHandler {
  constructor(maxRetries = 3, baseDelay = 1000, strategy = 'exponential') {
    this.maxRetries = maxRetries;
    this.baseDelay = baseDelay;
    this.retryCount = 0;
    this.strategy = strategy; // 'exponential', 'linear', 'fixed'
    this.circuitBreakerThreshold = 5;
    this.circuitBreakerWindow = 60000; // 1 minute
    this.failureHistory = [];
    this.lastSuccessTime = Date.now();
  }

  async execute(fn, context = 'general') {
    // Check circuit breaker
    if (this.isCircuitOpen()) {
      throw new Error('Circuit breaker is open - too many recent failures');
    }

    while (this.retryCount <= this.maxRetries) {
      try {
        const result = await fn();
        this.onSuccess();
        return result;
      } catch (error) {
        this.onFailure(error);
        
        const errorInfo = getErrorMessage(error, context);
        
        // Don't retry if error type doesn't support retrying
        if (!errorInfo.canRetry || this.retryCount >= this.maxRetries) {
          throw {
            ...error,
            retryCount: this.retryCount,
            maxRetries: this.maxRetries,
            errorInfo,
            circuitBreakerOpen: this.isCircuitOpen()
          };
        }

        this.retryCount++;
        
        // Calculate delay based on strategy
        const delay = this.calculateDelay();
        
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  calculateDelay() {
    let delay;
    
    switch (this.strategy) {
      case 'exponential':
        delay = this.baseDelay * Math.pow(2, this.retryCount - 1);
        break;
      case 'linear':
        delay = this.baseDelay * this.retryCount;
        break;
      case 'fixed':
      default:
        delay = this.baseDelay;
        break;
    }

    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * delay;
    return Math.min(delay + jitter, 30000); // Cap at 30 seconds
  }

  onSuccess() {
    this.retryCount = 0;
    this.lastSuccessTime = Date.now();
    // Clear old failures from history
    this.cleanFailureHistory();
  }

  onFailure(error) {
    this.failureHistory.push({
      timestamp: Date.now(),
      error: error.message || error.toString()
    });
    this.cleanFailureHistory();
  }

  cleanFailureHistory() {
    const cutoff = Date.now() - this.circuitBreakerWindow;
    this.failureHistory = this.failureHistory.filter(
      failure => failure.timestamp > cutoff
    );
  }

  isCircuitOpen() {
    this.cleanFailureHistory();
    return this.failureHistory.length >= this.circuitBreakerThreshold;
  }

  reset() {
    this.retryCount = 0;
    this.failureHistory = [];
    this.lastSuccessTime = Date.now();
  }

  canRetry() {
    return this.retryCount < this.maxRetries && !this.isCircuitOpen();
  }

  getStats() {
    return {
      retryCount: this.retryCount,
      maxRetries: this.maxRetries,
      recentFailures: this.failureHistory.length,
      circuitBreakerOpen: this.isCircuitOpen(),
      lastSuccessTime: this.lastSuccessTime,
      strategy: this.strategy
    };
  }
}

/**
 * Error logging service
 */
export const logError = (error, context = {}) => {
  const errorInfo = {
    message: error.message || error.toString(),
    stack: error.stack,
    timestamp: new Date().toISOString(),
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', errorInfo);
  }

  // In production, you might want to send to an error tracking service
  // Example: Sentry, LogRocket, etc.
  // if (process.env.NODE_ENV === 'production') {
  //   sendToErrorService(errorInfo);
  // }
};

/**
 * Create enhanced error object with additional context
 */
export const createEnhancedError = (originalError, context = 'general', additionalInfo = {}) => {
  const errorInfo = getErrorMessage(originalError, context);
  
  return {
    ...originalError,
    type: categorizeError(originalError),
    context,
    errorInfo,
    timestamp: new Date().toISOString(),
    ...additionalInfo
  };
};

/**
 * Enhanced error recovery utilities
 */
export const ErrorRecovery = {
  /**
   * Attempt to recover from common errors
   */
  async attemptRecovery(error, context = {}) {
    const errorType = categorizeError(error);
    
    switch (errorType) {
      case ERROR_TYPES.NETWORK:
        // Wait for network to be available
        if (navigator.onLine === false) {
          await this.waitForNetwork();
        }
        break;
        
      case ERROR_TYPES.CLIENT:
        // Clear relevant caches
        if ('caches' in window) {
          try {
            const cacheNames = await caches.keys();
            await Promise.all(
              cacheNames.map(name => caches.delete(name))
            );
          } catch (cacheError) {
            console.warn('Failed to clear caches:', cacheError);
          }
        }
        break;
        
      default:
        // No specific recovery action
        break;
    }
  },

  /**
   * Wait for network connectivity
   */
  waitForNetwork(timeout = 30000) {
    return new Promise((resolve, reject) => {
      if (navigator.onLine) {
        resolve();
        return;
      }

      const timeoutId = setTimeout(() => {
        window.removeEventListener('online', onOnline);
        reject(new Error('Network timeout'));
      }, timeout);

      const onOnline = () => {
        clearTimeout(timeoutId);
        window.removeEventListener('online', onOnline);
        resolve();
      };

      window.addEventListener('online', onOnline);
    });
  },

  /**
   * Check if error is recoverable
   */
  isRecoverable(error) {
    const errorType = categorizeError(error);
    const recoverableTypes = [
      ERROR_TYPES.NETWORK,
      ERROR_TYPES.CLIENT,
      ERROR_TYPES.SERVER
    ];
    return recoverableTypes.includes(errorType);
  }
};

/**
 * Enhanced hook for handling errors in components
 */
export const useErrorHandler = (context = 'general') => {
  const handleError = (error, additionalInfo = {}) => {
    const enhancedError = createEnhancedError(error, context, additionalInfo);
    logError(enhancedError, { context, ...additionalInfo });
    return enhancedError;
  };

  const createRetryHandler = (maxRetries = 3, baseDelay = 1000, strategy = 'exponential') => {
    return new RetryHandler(maxRetries, baseDelay, strategy);
  };

  const handleErrorWithRecovery = async (error, additionalInfo = {}) => {
    const enhancedError = handleError(error, additionalInfo);
    
    // Attempt recovery for recoverable errors
    if (ErrorRecovery.isRecoverable(error)) {
      try {
        await ErrorRecovery.attemptRecovery(error, { context, ...additionalInfo });
      } catch (recoveryError) {
        console.warn('Error recovery failed:', recoveryError);
      }
    }
    
    return enhancedError;
  };

  return {
    handleError,
    handleErrorWithRecovery,
    createRetryHandler,
    getErrorMessage: (error) => getErrorMessage(error, context),
    categorizeError,
    ErrorRecovery
  };
};